import type { LayoutElement } from '../../types';
import type { BrowserKineticVehicleLeg } from '../../types/simulationPlayback';
import { buildTrackGraph, type TrackEdge } from '../../utils/simulationGraph';
import {
  getExportableTrackEdges,
  simulationTrackNodeId,
} from '../../utils/simulationTrackNetworkView';

/**
 * Streamed node coordinates can sit a couple of units off a rail's drawn
 * centerline (merged joints keep the first endpoint seen). Poses farther than
 * this from the matched rail are left as streamed rather than dragged across.
 */
export const MAX_RAIL_SNAP_DIST = 4;

/** A rail and the direction a leg travels along it. */
export type RailTravel = {
  edge: TrackEdge;
  /** True when travelling from `edge.points[0]` towards the last point. */
  forward: boolean;
};

export type RailPose = { x: number; y: number; angle: number };

type Projection = {
  x: number;
  y: number;
  dist: number;
  /** Arc length from the first point. */
  s: number;
  /** Index of the polyline piece the projection landed on. */
  piece: number;
};

function projectOntoPolyline(points: readonly { x: number; y: number }[], px: number, py: number): Projection | null {
  if (points.length < 2) return null;
  let best: Projection | null = null;
  let travelled = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len > 0) {
      const t = Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / (len * len)));
      const x = a.x + dx * t;
      const y = a.y + dy * t;
      const dist = Math.hypot(px - x, py - y);
      if (!best || dist < best.dist) best = { x, y, dist, s: travelled + len * t, piece: i };
    }
    travelled += len;
  }
  return best;
}

/**
 * A pose further than this off every nearby rail is treated as a real turn
 * (corner blend, turntable) rather than a skew to correct.
 */
export const MAX_RAIL_ALIGN_DEG = 20;

const GRID_CELL = 64;

const gridCell = (v: number): number => Math.floor(v / GRID_CELL);

const normalizeDeg = (deg: number): number => {
  const n = ((deg % 360) + 360) % 360;
  return n > 360 - 1e-9 ? 0 : n;
};

/** Shortest signed turn from `fromDeg` to `toDeg`, in (-180, 180]. */
export function signedTurnDeg(fromDeg: number, toDeg: number): number {
  const d = normalizeDeg(toDeg - fromDeg);
  return d > 180 ? d - 360 : d;
}

function nodePairKey(fromId: string, toId: string): string {
  return `${fromId}|${toId}`;
}

/** Looks up the drawn rail a live leg runs on, using the same ids sent to the backend. */
export class LiveRailIndex {
  private readonly bySegmentId = new Map<string, TrackEdge>();
  private readonly byNodePair = new Map<string, TrackEdge[]>();
  /** Every drawn edge (curves included) bucketed by grid cell, for nearest-rail lookups. */
  private readonly grid = new Map<string, TrackEdge[]>();
  private edgeCount = 0;

  constructor(elements: readonly LayoutElement[]) {
    const list = [...elements];
    const graph = buildTrackGraph(list);
    for (const { edge, segmentId } of getExportableTrackEdges(graph, list)) {
      this.bySegmentId.set(segmentId, edge);
      const a = simulationTrackNodeId(edge.a);
      const b = simulationTrackNodeId(edge.b);
      for (const key of [nodePairKey(a, b), nodePairKey(b, a)]) {
        const bucket = this.byNodePair.get(key);
        if (bucket) bucket.push(edge);
        else this.byNodePair.set(key, [edge]);
      }
    }
    for (const edge of graph.edges) this.addToGrid(edge);
  }

  get isEmpty(): boolean {
    return this.edgeCount === 0;
  }

  private addToGrid(edge: TrackEdge): void {
    if (edge.points.length < 2) return;
    this.edgeCount += 1;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of edge.points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    const c0x = gridCell(minX - MAX_RAIL_SNAP_DIST);
    const c1x = gridCell(maxX + MAX_RAIL_SNAP_DIST);
    const c0y = gridCell(minY - MAX_RAIL_SNAP_DIST);
    const c1y = gridCell(maxY + MAX_RAIL_SNAP_DIST);
    for (let cx = c0x; cx <= c1x; cx++) {
      for (let cy = c0y; cy <= c1y; cy++) {
        const key = `${cx},${cy}`;
        const bucket = this.grid.get(key);
        if (bucket) bucket.push(edge);
        else this.grid.set(key, [edge]);
      }
    }
  }

  /**
   * Seats a pose on the closest drawn rail that runs within
   * `MAX_RAIL_ALIGN_DEG` of `headingDeg`, facing whichever way along it is
   * nearer that heading. Used when the leg itself could not be matched to a
   * rail (curves, multi-segment legs, parked vehicles), so a heading that is
   * genuinely mid-turn is left alone.
   */
  nearestAlignedPose(x: number, y: number, headingDeg: number): RailPose | null {
    const bucket = this.grid.get(`${gridCell(x)},${gridCell(y)}`);
    if (!bucket) return null;
    let best: (RailPose & { dist: number }) | null = null;
    for (const edge of bucket) {
      const hit = projectOntoPolyline(edge.points, x, y);
      if (!hit || hit.dist > MAX_RAIL_SNAP_DIST || (best && hit.dist >= best.dist)) continue;
      const a = edge.points[hit.piece];
      const b = edge.points[hit.piece + 1];
      const tangent = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
      const forwardTurn = Math.abs(signedTurnDeg(headingDeg, tangent));
      const backwardTurn = Math.abs(signedTurnDeg(headingDeg, tangent + 180));
      if (Math.min(forwardTurn, backwardTurn) > MAX_RAIL_ALIGN_DEG) continue;
      const angle = normalizeDeg(forwardTurn <= backwardTurn ? tangent : tangent + 180);
      best = { x: hit.x, y: hit.y, angle, dist: hit.dist };
    }
    return best ? { x: best.x, y: best.y, angle: best.angle } : null;
  }

  /** Rail + direction for a moving leg, or null when it cannot be matched safely. */
  travelForLeg(leg: BrowserKineticVehicleLeg): RailTravel | null {
    if (leg.fromX === leg.toX && leg.fromY === leg.toY) return null;
    const bySegment = leg.segmentId ? this.bySegmentId.get(leg.segmentId) : undefined;
    const candidates = bySegment
      ? [bySegment]
      : (this.byNodePair.get(nodePairKey(leg.fromId, leg.toId)) ?? []);

    let best: { travel: RailTravel; error: number } | null = null;
    for (const edge of candidates) {
      const from = projectOntoPolyline(edge.points, leg.fromX, leg.fromY);
      const to = projectOntoPolyline(edge.points, leg.toX, leg.toY);
      if (!from || !to) continue;
      const error = Math.max(from.dist, to.dist);
      if (error > MAX_RAIL_SNAP_DIST || Math.abs(to.s - from.s) < 1e-6) continue;
      if (!best || error < best.error) {
        best = { travel: { edge, forward: to.s > from.s }, error };
      }
    }
    return best?.travel ?? null;
  }
}

/**
 * Nearest point on the rail centerline and the rail's heading there, in the
 * direction of travel. Null when the point is too far from the rail to trust.
 */
export function snapPoseToRail(travel: RailTravel, x: number, y: number): RailPose | null {
  const { points } = travel.edge;
  const hit = projectOntoPolyline(points, x, y);
  if (!hit || hit.dist > MAX_RAIL_SNAP_DIST) return null;
  const a = points[hit.piece];
  const b = points[hit.piece + 1];
  const deg = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  return { x: hit.x, y: hit.y, angle: normalizeDeg(travel.forward ? deg : deg + 180) };
}
